import { useWindowWidth } from '@liveauctioneers/hammer-ui-core/providers';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useEffect } from 'react';
import useMeasure from 'react-use-measure';

import { CategoryModule } from '@/components/CategoryModule/CategoryModule';
import { ComingUpCarousel } from '@/components/ComingUp/ComingUpCarousel';
import { CompleteAccountBanner } from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import { FeaturedAuctioneers } from '@/components/FeaturedAuctioneers/FeaturedAuctioneers';
import FeaturedCatalogCarousel from '@/components/FeaturedCatalogCarousel/FeaturedCatalogCarousel';
import { FollowedSearchCarousel } from '@/components/FollowedSearchCarousel/FollowedSearchCarousel';
import { HeroBanner } from '@/components/Hero/HeroBanner';
import { RecentItemsCarousel } from '@/components/RecentItems/RecentItemsCarousel';
import { RecommendedItemCarousel } from '@/components/RecommendedItem/RecommendedItemCarousel';
import { SearchFollowSuggestions } from '@/components/SearchFollowSuggestions/SearchFollowSuggestions';
import { TrendingItemsCarousel } from '@/components/TrendingItems/TrendingItemsCarousel';
import { AuctionsYouMayLikeCarousel } from '@/components/YouMayLikeCarousel/AuctionsYouMayLikeCarousel';
import { YourFollowedSearches } from '@/components/YourFollowedSearches/YourFollowedSearches';
import { usePrevious } from '@/hooks/usePrevious';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { fetchAllHomeData } from '@/redux/modules/home/home.actions';
import { getFeaturedAuctioneersExist } from '@/redux/modules/home/home.selectors';
import { useHomePageRedesignExperiment } from '@/utils/amplitude/experiments/hooks/useHomePageRedesign.experiment';
import { useVariantJumpingHomePageCheckExperiment } from '@/utils/amplitude/experiments/hooks/useVariantJumpingHomePageCheckExperiment.experiment';

import HomePageHeaderMeta from './HomePageHeaderMeta';
import { HomePageUpcomingCatalogs } from './HomePageUpcomingCatalogs';

const sharedSectionClasses = 'mdMax:w-[calc(100%-40px)] w-full mx-auto my-sm max-w-[1200px]';
const sectionClasses = 'w-[calc(100%-40px)] max-w-[1200px] mx-auto my-sm';
const redesignSectionClasses = 'mx-auto my-0 w-full max-w-full px-sm smMin:px-md';

const HomePage = () => {
    const dispatch = useAppDispatch();
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const wasLoggedOut = usePrevious(!isLoggedIn);
    const featuredAuctioners = useAppSelector(getFeaturedAuctioneersExist);
    const initialWidth = useWindowWidth();
    const [featuredCatalogsRef, { width }] = useMeasure();
    useEffect(() => {
        // if you were logged out when you landed on this page, we need to reload the data
        if (wasLoggedOut && isLoggedIn) {
            dispatch(fetchAllHomeData());
        }
    }, [isLoggedIn, dispatch, wasLoggedOut]);

    // Mainly for testing variant jumping on home page
    useVariantJumpingHomePageCheckExperiment();

    // Experiment: SEAR-1758 Home Page Card & Layout Redesign (logged-in + logged-out).
    const shouldUseHomePageRedesign = useHomePageRedesignExperiment();
    const mainContainerClassesExperiment = 'mx-auto max-w-[1644px] gap-lg smMin:gap-xl';

    return (
        <section
            className={cn(
                'relative flex w-full flex-col content-center',
                shouldUseHomePageRedesign && mainContainerClassesExperiment
            )}
        >
            <HomePageHeaderMeta />
            {isLoggedIn && (
                <div className={cn(shouldUseHomePageRedesign && redesignSectionClasses)}>
                    <CompleteAccountBanner
                        className={cn(
                            'mx-auto mt-md w-[calc(100%-40px)] max-w-[1200px]',
                            shouldUseHomePageRedesign && 'mt-0 w-full max-w-full smMax:mx-auto smMax:w-full'
                        )}
                    />
                </div>
            )}
            {!isLoggedIn && (
                <div
                    className={cn(
                        shouldUseHomePageRedesign && redesignSectionClasses,
                        shouldUseHomePageRedesign && 'pt-lg xsMax:pt-md'
                    )}
                >
                    <HeroBanner shouldUseHomePageRedesign={shouldUseHomePageRedesign} />
                </div>
            )}
            <div className={shouldUseHomePageRedesign ? redesignSectionClasses : cn(sharedSectionClasses, 'mb-md')}>
                <div
                    className="w-full"
                    ref={featuredCatalogsRef}
                >
                    <FeaturedCatalogCarousel
                        shouldUseHomePageRedesign={shouldUseHomePageRedesign}
                        width={width || initialWidth}
                    />
                </div>
            </div>
            {isLoggedIn && (
                <ComingUpCarousel
                    className={shouldUseHomePageRedesign ? redesignSectionClasses : sharedSectionClasses}
                    shouldUseHomePageRedesign={shouldUseHomePageRedesign}
                    testid="comingUpForAuctions"
                />
            )}
            {featuredAuctioners && (
                <div className={cn(shouldUseHomePageRedesign && redesignSectionClasses)}>
                    <FeaturedAuctioneers shouldUseHomePageRedesign={shouldUseHomePageRedesign} />
                </div>
            )}
            {isLoggedIn && (
                <>
                    {shouldUseHomePageRedesign && <YourFollowedSearches className={redesignSectionClasses} />}
                    <FollowedSearchCarousel
                        className={shouldUseHomePageRedesign ? redesignSectionClasses : sectionClasses}
                        shouldUseHomePageRedesign={shouldUseHomePageRedesign}
                    />
                </>
            )}
            {isLoggedIn && (
                <RecommendedItemCarousel
                    className={shouldUseHomePageRedesign ? redesignSectionClasses : sharedSectionClasses}
                    shouldUseHomePageRedesign={shouldUseHomePageRedesign}
                />
            )}
            {isLoggedIn && (
                <AuctionsYouMayLikeCarousel
                    className={shouldUseHomePageRedesign ? redesignSectionClasses : sharedSectionClasses}
                    shouldUseHomePageRedesign={shouldUseHomePageRedesign}
                />
            )}
            <SearchFollowSuggestions
                className={shouldUseHomePageRedesign ? redesignSectionClasses : sharedSectionClasses}
                shouldUseHomePageRedesign={shouldUseHomePageRedesign}
            />
            <RecentItemsCarousel
                className={shouldUseHomePageRedesign ? redesignSectionClasses : sharedSectionClasses}
                shouldUseHomePageRedesign={shouldUseHomePageRedesign}
            />
            <CategoryModule
                className={
                    shouldUseHomePageRedesign ? redesignSectionClasses : cn(sharedSectionClasses, sectionClasses)
                }
                shouldUseHomePageRedesign={shouldUseHomePageRedesign}
                testid="categoryModule"
            />
            <TrendingItemsCarousel
                className={shouldUseHomePageRedesign ? redesignSectionClasses : sharedSectionClasses}
                shouldUseHomePageRedesign={shouldUseHomePageRedesign}
            />
            <HomePageUpcomingCatalogs
                className={shouldUseHomePageRedesign ? redesignSectionClasses : sharedSectionClasses}
                shouldUseHomePageRedesign={shouldUseHomePageRedesign}
            />
        </section>
    );
};

export default HomePage;
